import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Wallet, CheckCircle2, FileStack, FileText,
  Receipt, FileSignature, ArrowRight, Plus, Clock, AlertCircle
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { useData } from '../context/DataContext';
import { fmtRM, fmtDate, computeTotals } from '../lib/store';
import { StatusBadge, Card } from '../components/Shared';
import { Button } from '../components/ui/button';

const BigStat = ({ label, value, delta, deltaDir, color, icon: Icon }) => (
  <div className={`relative overflow-hidden rounded-xl p-5 text-white shadow-lg ${color}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-white/80">{label}</p>
        <p className="mt-2 text-3xl font-extrabold tracking-tight">{value}</p>
        {delta && (
          <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-white/90">
            {deltaDir === 'up' ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {delta}
          </div>
        )}
      </div>
      <div className="rounded-lg bg-white/20 p-2.5"><Icon className="h-6 w-6" /></div>
    </div>
  </div>
);

const MiniStat = ({ label, value, sub, icon: Icon, tint }) => (
  <Card className="p-4">
    <div className="flex items-center gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-400">{sub}</p>}
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const nav = useNavigate();
  const { cashflow, quotations, invoices, receipts, vouchers, proposals, company, getCustomer } = useData();

  const pendingQ = quotations.filter((q) => q.status === 'pending' || q.status === 'draft').length;
  const unpaidI = invoices.filter((i) => i.status !== 'paid').length;
  const draftV = vouchers.filter((v) => v.status === 'draft').length;

  const now = new Date();
  const inMonth = (iso, monthsBack = 0) => {
    if (!iso) return false;
    const d = new Date(iso);
    const ref = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
  };
  const invoiceTotal = (inv) => computeTotals(inv.items, company.sstRate).total;
  const sum = (arr, fn) => arr.reduce((t, x) => t + fn(x), 0);
  const pct = (curr, prev) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null);

  const salesThisMonth = sum(invoices.filter((i) => inMonth(i.date)), invoiceTotal);
  const salesLastMonth = sum(invoices.filter((i) => inMonth(i.date, 1)), invoiceTotal);
  const outstanding = sum(invoices, (i) => Math.max(0, invoiceTotal(i) - Number(i.amountPaid || 0)));
  const paymentsThisMonth = sum(receipts.filter((r) => inMonth(r.date)), (r) => Number(r.amount || 0));
  const paymentsLastMonth = sum(receipts.filter((r) => inMonth(r.date, 1)), (r) => Number(r.amount || 0));
  const totalDocs = quotations.length + invoices.length + receipts.length + vouchers.length + proposals.length;
  const receiptsThisMonth = receipts.filter((r) => inMonth(r.date)).length;

  const salesDelta = pct(salesThisMonth, salesLastMonth);
  const paymentsDelta = pct(paymentsThisMonth, paymentsLastMonth);
  const collectPct = salesThisMonth > 0 ? Math.round((outstanding / (salesThisMonth + outstanding)) * 100) : 0;

  const upcoming = invoices
    .filter((i) => i.status !== 'paid' && i.dueDate)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 3)
    .map((i) => {
      const days = Math.round((new Date(i.dueDate) - now) / 86400000);
      const overdue = days < 0;
      return {
        n: i.number,
        a: fmtRM(Math.max(0, invoiceTotal(i) - Number(i.amountPaid || 0))),
        s: overdue ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`
          : days === 0 ? 'Due today' : `Due in ${days} day${days === 1 ? '' : 's'}`,
        c: overdue ? 'text-red-600' : days <= 3 ? 'text-amber-600' : 'text-slate-500',
        i: overdue ? AlertCircle : Clock,
      };
    });

  const recent = [
    ...invoices.slice(0, 2).map((i) => ({ type: 'INV', ...i })),
    ...quotations.slice(0, 1).map((q) => ({ type: 'QT', ...q })),
    ...vouchers.slice(0, 1).map((v) => ({ type: 'PV', ...v })),
  ];

  const today = new Date().toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });

  const quickActions = [
    { label: 'Quotation', icon: FileText, to: '/quotations', tint: 'bg-blue-100 text-blue-600' },
    { label: 'Invoice', icon: FileSignature, to: '/invoices', tint: 'bg-violet-100 text-violet-600' },
    { label: 'Receipt', icon: Receipt, to: '/receipts', tint: 'bg-pink-100 text-pink-600' },
    { label: 'Voucher', icon: Wallet, to: '/vouchers', tint: 'bg-orange-100 text-orange-600' },
    { label: 'Proposal', icon: FileStack, to: '/proposals', tint: 'bg-emerald-100 text-emerald-600' },
  ];

  return (
    <div className="animate-fade-in-up space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Good morning! Here's what's happening with your business.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-500">Today &nbsp;·&nbsp; {today}</div>
      </div>

      {/* Big stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <BigStat label="Total Sales (This Month)" value={fmtRM(salesThisMonth)} delta={salesDelta !== null ? `${Math.abs(salesDelta)}% vs last month` : null} deltaDir={salesDelta !== null && salesDelta < 0 ? 'down' : 'up'} color="bg-gradient-to-br from-blue-500 to-blue-600" icon={TrendingUp} />
        <BigStat label="Outstanding" value={fmtRM(outstanding)} delta={outstanding > 0 ? `${collectPct}% to collect` : 'All settled'} deltaDir="down" color="bg-gradient-to-br from-emerald-500 to-emerald-600" icon={Wallet} />
        <BigStat label="Payments Received" value={fmtRM(paymentsThisMonth)} delta={paymentsDelta !== null ? `${Math.abs(paymentsDelta)}% vs last month` : null} deltaDir={paymentsDelta !== null && paymentsDelta < 0 ? 'down' : 'up'} color="bg-gradient-to-br from-violet-500 to-violet-600" icon={CheckCircle2} />
        <BigStat label="Total Documents" value={totalDocs} delta={`${unpaidI} awaiting payment`} deltaDir="up" color="bg-gradient-to-br from-orange-500 to-orange-600" icon={FileStack} />
      </div>

      {/* Mini stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MiniStat label="Quotations" value={quotations.length} sub={`${pendingQ} pending`} icon={FileText} tint="bg-blue-100 text-blue-600" />
        <MiniStat label="Invoices" value={invoices.length} sub={`${unpaidI} unpaid`} icon={FileSignature} tint="bg-violet-100 text-violet-600" />
        <MiniStat label="Receipts" value={receipts.length} sub={`${receiptsThisMonth} this month`} icon={Receipt} tint="bg-pink-100 text-pink-600" />
        <MiniStat label="Vouchers" value={vouchers.length} sub={`${draftV} in draft`} icon={Wallet} tint="bg-orange-100 text-orange-600" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Cash flow chart */}
        <Card className="p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Cash Flow Trend</h2>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cashflow} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `RM ${v / 1000}k`} />
                <Tooltip formatter={(v) => fmtRM(v)} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line type="monotone" dataKey="sales" name="Sales" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="payments" name="Payments" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="outstanding" name="Outstanding" stroke="#f97316" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Recent activity */}
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Recent Activity</h2>
            <button onClick={() => nav('/invoices')} className="text-xs font-medium text-blue-600 hover:underline">View all</button>
          </div>
          <div className="space-y-3">
            {recent.map((r, idx) => {
              const cust = getCustomer(r.customerId);
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-600">{r.type}</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{r.number}</p>
                    <p className="truncate text-xs text-slate-400">{cust?.name || r.payee || 'ACME'}</p>
                  </div>
                  {r.status && <StatusBadge status={r.status} />}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Quick actions + upcoming */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <h2 className="mb-4 font-semibold text-slate-800">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            {quickActions.map((a) => (
              <button key={a.label} onClick={() => nav(a.to)} className="group flex min-w-[110px] flex-1 flex-col items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:bg-white hover:shadow-md">
                <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${a.tint}`}><a.icon className="h-5 w-5" /></div>
                <span className="text-sm font-medium text-slate-700">{a.label}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Upcoming Due</h2>
            <button onClick={() => nav('/invoices')} className="text-xs font-medium text-blue-600 hover:underline">View all</button>
          </div>
          <div className="space-y-3">
            {upcoming.length === 0 && <p className="text-sm text-slate-400">No unpaid invoices with a due date.</p>}
            {upcoming.map((u) => (
              <div key={u.n} className="flex items-center gap-3">
                <u.i className={`h-4 w-4 shrink-0 ${u.c}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{u.n}</p>
                  <p className={`text-xs ${u.c}`}>{u.s}</p>
                </div>
                <span className="text-sm font-semibold text-slate-700">{u.a}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
